/**
 * Video cover (thumbnail) for a social post.
 *
 * WHY THREE MODES AND NOT ONE. Zernio accepts either a custom image or a frame
 * offset, and people genuinely want both: most want "the good bit, about four
 * seconds in", while a practice with a designed cover graphic wants to upload
 * it. Offering only upload forces the first group to export a still from their
 * own video first; offering only an offset locks the second group out.
 *
 * EVERY COVER IS CROPPED HERE. Both a picked moment and an uploaded photo go
 * through CoverCropDialog, held to the video's shape, and the cropped JPEG is
 * sent as the cover image. A cover of another shape is cropped again by the
 * networks; before there is a video the shape is unknown and the crop is free.
 * So a photo cropped before the video was added, or kept when the video was
 * swapped for one of another shape, can still be off: the picker compares the
 * two and offers to crop the cover again, held to the video's shape.
 *
 * "Pick a moment" scrubs the video and captures that frame. Capturing reads the
 * video's pixels, which the browser only allows when its storage permits
 * cross-origin reads; when it does not, the chosen moment is still sent as a
 * timestamp (Zernio's offset), and the network crops that frame itself.
 *
 * SHOWN BEFORE THE VIDEO. A thumbnail photo can be set before there is a video;
 * "Pick a moment" waits until there is one. On a post that ends up photos-only
 * it is not sent (every platform ignores it there); the callers drop it on save.
 *
 * AN IMAGE ALWAYS BEATS AN OFFSET, which is Zernio's own precedence, so the
 * modes are mutually exclusive: choosing one clears the other.
 */
import { useEffect, useRef, useState } from 'react';
import { Crop, Image as ImageIcon, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { toast } from '@/store/toastStore';
import { getErrorMessage } from '@/services/apiClient';
import { uploadZernioMedia, validateMediaFile } from '@/services/zernio.service';
import { CoverCropDialog } from './CoverCropDialog';

export interface CoverSelection {
  imageUrl?: string;
  /** A signed display link, on PR previews whose bucket is private. Shown, never saved or sent. */
  previewUrl?: string;
  offsetMs?: number;
}

type Mode = 'auto' | 'frame' | 'image';

interface Props {
  value: CoverSelection | null;
  onChange: (value: CoverSelection | null) => void;
  /** Whether the post has a video. Without one, only a thumbnail photo can be set yet. */
  hasVideo: boolean;
  /** The video a moment is picked from — the post's first video. */
  videoUrl?: string | null;
  /** How many items the post has; more than one is a carousel. */
  itemCount?: number;
  onUploadingChange?: (uploading: boolean) => void;
  disabled?: boolean;
}

/** Images only — a cover is a still, and Zernio rejects anything else. */
const COVER_ACCEPT = 'image/jpeg,image/png,image/webp';

function modeOf(value: CoverSelection | null): Mode {
  if (value?.imageUrl) return 'image';
  if (value?.offsetMs !== undefined) return 'frame';
  return 'auto';
}

/**
 * The frame at `seconds`, as a JPEG blob — or null when the browser refuses to
 * read the video's pixels (its storage does not allow cross-origin reads).
 */
function captureFrame(url: string, seconds: number): Promise<Blob | null> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    let settled = false;
    const done = (blob: Blob | null) => {
      if (settled) return;
      settled = true;
      video.removeAttribute('src');
      video.load();
      resolve(blob);
    };
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';
    video.onloadeddata = () => {
      video.currentTime = Math.min(seconds, Math.max(0, (video.duration || seconds) - 0.05));
    };
    video.onseeked = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx || !canvas.width) return done(null);
        ctx.drawImage(video, 0, 0);
        canvas.toBlob((blob) => done(blob), 'image/jpeg', 0.95);
      } catch {
        done(null); // a tainted canvas: the storage does not allow the read
      }
    };
    video.onerror = () => done(null);
    setTimeout(() => done(null), 20000);
    video.src = url;
  });
}

function useVideoAspect(url?: string | null): number | null {
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    setAspect(null);
    if (!url) return;
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    video.onloadedmetadata = () => {
      if (video.videoWidth && video.videoHeight) setAspect(video.videoWidth / video.videoHeight);
    };
    video.src = url;
    return () => {
      video.onloadedmetadata = null;
      video.removeAttribute('src');
      video.load();
    };
  }, [url]);
  return aspect;
}

/** The picture's width ÷ height, once it has loaded; null until then or without one. */
function useImageAspect(url?: string | null): number | null {
  const [aspect, setAspect] = useState<number | null>(null);
  useEffect(() => {
    setAspect(null);
    if (!url) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) setAspect(img.naturalWidth / img.naturalHeight);
    };
    img.src = url;
    return () => {
      img.onload = null;
    };
  }, [url]);
  return aspect;
}

/**
 * How far a cover's shape may differ from the video's and still count as the
 * same: a crop rounded to whole pixels lands a hair off (608×1081 is 0.5624
 * against 9:16's 0.5625), and that is not worth a warning.
 */
const SHAPE_TOLERANCE = 0.02;

export function ZernioCoverPicker({
  value,
  onChange,
  hasVideo,
  videoUrl,
  itemCount = 1,
  onUploadingChange,
  disabled,
}: Props) {
  const [uploading, setUploading] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [reopening, setReopening] = useState(false);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [duration, setDuration] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const scrubRef = useRef<HTMLVideoElement>(null);
  const reopenFor = useRef<string | null>(null);
  const videoAspect = useVideoAspect(hasVideo ? videoUrl : null);
  const mode = modeOf(value);
  const seconds = value?.offsetMs !== undefined ? value.offsetMs / 1000 : 0;
  const coverSrc = value?.imageUrl ? value.previewUrl ?? value.imageUrl : null;
  const coverAspect = useImageAspect(coverSrc);
  const shapeOff =
    mode === 'image' && !!videoAspect && !!coverAspect && Math.abs(coverAspect / videoAspect - 1) > SHAPE_TOLERANCE;

  // Release the object URL the crop dialog was showing.
  useEffect(() => () => { if (cropSrc?.startsWith('blob:')) URL.revokeObjectURL(cropSrc); }, [cropSrc]);

  useEffect(() => {
    reopenFor.current = null;
  }, [coverSrc]);

  const setMode = (next: Mode) => {
    if (next === 'auto') onChange(null);
    // Seeding 0 rather than leaving it undefined is what keeps the mode
    // selected: "first frame" is a real choice, distinct from "no cover set".
    else if (next === 'frame') onChange({ offsetMs: value?.offsetMs ?? 0 });
    else fileRef.current?.click();
  };

  const pickPhoto = (file: File | undefined) => {
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    reopenFor.current = null;
    const problem = validateMediaFile(file);
    if (problem) {
      toast.error(problem);
      return;
    }
    setCropSrc(URL.createObjectURL(file));
  };

  const scrubTo = (next: number) => {
    onChange({ offsetMs: Math.max(0, Math.round(next * 1000)) });
    if (scrubRef.current) scrubRef.current.currentTime = next;
  };

  const cropMoment = async () => {
    if (!videoUrl) return;
    setCapturing(true);
    const blob = await captureFrame(videoUrl, seconds);
    setCapturing(false);
    if (!blob) {
      toast.warning(
        "That frame couldn't be captured here, so the moment will be sent as a timestamp and each network crops it itself. Upload a thumbnail photo to choose the crop exactly.",
      );
      return;
    }
    setCropSrc(URL.createObjectURL(blob));
  };

  // Re-crops the cover already set. It is fetched rather than shown by URL
  // because the crop reads its pixels, which needs a CORS read; no-store keeps
  // the browser from answering with a copy it cached without CORS headers.
  const recropCover = async () => {
    const src = coverSrc;
    if (!src) return;
    reopenFor.current = src;
    setReopening(true);
    try {
      const res = await fetch(src, { mode: 'cors', cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (reopenFor.current !== src) return;
      setCropSrc(URL.createObjectURL(blob));
    } catch (e) {
      if (reopenFor.current !== src) return;
      console.error('[cover] could not reopen the thumbnail for cropping', e);
      toast.warning("That thumbnail couldn't be opened for cropping here. Upload the photo again to crop it to the video's shape.");
    } finally {
      if (reopenFor.current === src || reopenFor.current === null) {
        reopenFor.current = null;
        setReopening(false);
      }
    }
  };

  const uploadCrop = async (blob: Blob) => {
    setUploading(true);
    onUploadingChange?.(true);
    try {
      const media = await uploadZernioMedia(new File([blob], 'thumbnail.jpg', { type: 'image/jpeg' }));
      // Replaces any offset: the two are mutually exclusive, and Zernio would
      // ignore the offset anyway once an image is present.
      onChange({ imageUrl: media.url, ...(media.previewUrl ? { previewUrl: media.previewUrl } : {}) });
      setCropSrc(null);
    } catch (e) {
      toast.error(getErrorMessage(e, 'That thumbnail could not be uploaded.'));
    } finally {
      setUploading(false);
      onUploadingChange?.(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 p-3 space-y-2.5">
      <div className="flex items-center gap-2">
        <ImageIcon className="h-4 w-4 text-slate-400" />
        <span className="text-sm font-medium text-slate-700">Thumbnail</span>
        <span className="text-xs text-slate-400">
          The photo shown before the video plays — for Instagram and Facebook Reels
        </span>
      </div>

      <div className="inline-flex rounded-lg bg-slate-100 p-0.5">
        {([
          ['auto', 'First frame'],
          ['frame', 'Pick a moment'],
          ['image', 'Upload a thumbnail photo'],
        ] as Array<[Mode, string]>).map(([key, label]) => (
          <button
            key={key}
            type="button"
            disabled={disabled || uploading || (key === 'frame' && !hasVideo)}
            title={key === 'frame' && !hasVideo ? 'Add a video first — a moment is picked from it.' : undefined}
            onClick={() => setMode(key)}
            className={`px-3 py-1 text-sm rounded-md transition-colors disabled:opacity-50 ${
              mode === key ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {!hasVideo && (
        <p className="text-xs text-slate-500">
          Add a video to pick a moment from it. A thumbnail photo can be set now — it's used once there's a video.
        </p>
      )}

      {mode === 'frame' && hasVideo && videoUrl && (
        <div className="space-y-2">
          <video
            ref={scrubRef}
            src={videoUrl}
            crossOrigin="anonymous"
            muted
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => {
              setDuration(e.currentTarget.duration || 0);
              e.currentTarget.currentTime = seconds;
            }}
            className="max-h-56 rounded-md bg-black"
          />
          <div className="flex items-center gap-3">
            <input
              type="range"
              min={0}
              max={duration || 0}
              step={0.1}
              value={Math.min(seconds, duration || seconds)}
              disabled={disabled || !duration}
              onChange={(e) => scrubTo(Number(e.target.value))}
              className="flex-1"
              aria-label="Moment in the video"
            />
            <span className="w-16 text-right text-sm tabular-nums text-slate-600">{seconds.toFixed(1)}s</span>
          </div>
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled || capturing || uploading}
            loading={capturing}
            leftIcon={<Crop className="h-3.5 w-3.5" />}
            onClick={() => void cropMoment()}
          >
            Crop this frame
          </Button>
        </div>
      )}

      {mode === 'image' && value?.imageUrl && (
        <div className="flex items-center gap-3">
          <img
            src={value.previewUrl ?? value.imageUrl}
            alt=""
            className="h-20 max-w-[8rem] rounded-md border border-slate-200 object-contain bg-slate-50"
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={disabled}
            onClick={() => onChange(null)}
            leftIcon={<X className="h-3.5 w-3.5" />}
          >
            Remove
          </Button>
        </div>
      )}

      {shapeOff && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="flex-1 text-xs text-amber-800">
            This thumbnail is a different shape from your video, so Instagram and Facebook will crop it again.
            Crop it to the video&apos;s shape to choose what they show.
          </p>
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled || uploading || reopening}
            loading={reopening}
            leftIcon={<Crop className="h-3.5 w-3.5" />}
            onClick={() => void recropCover()}
          >
            Crop to fit the video
          </Button>
        </div>
      )}

      {uploading && (
        <p className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Uploading thumbnail&hellip;
        </p>
      )}

      {itemCount > 1 && (
        <p className="text-xs text-amber-700">
          This post is a carousel. Instagram ignores a custom thumbnail on carousels — it is used on
          the networks that support one.
        </p>
      )}

      {/* Facebook and LinkedIn read the cover off the media item, Instagram and
          TikTok from their own fields, and YouTube has no field at all — so the
          honest wording is "where supported" rather than naming platforms that
          would then drift. */}
      <p className="text-xs text-slate-400">
        Optional — leave it on First frame to skip it. Used mainly for Instagram and Facebook
        Reels, and on the other networks that support a custom thumbnail.
      </p>

      <input
        ref={fileRef}
        type="file"
        accept={COVER_ACCEPT}
        className="hidden"
        onChange={(e) => pickPhoto(e.target.files?.[0])}
      />

      <CoverCropDialog
        isOpen={cropSrc !== null}
        src={cropSrc}
        busy={uploading}
        aspect={videoAspect}
        onCancel={() => setCropSrc(null)}
        onDone={(blob) => void uploadCrop(blob)}
      />
    </div>
  );
}
